import React, { useRef } from "react";
import {
  AppBar,
  Button,
  Box,
  Toolbar,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import logoNoText from "../images/logo-notext.png";
import { useNavigate, NavLink } from "react-router-dom";
import { logoutGrafana, supabase } from "./SupabaseClient";
import { useProfile } from "../context/ProfileContext";
import {
  getInitials as computeInitials,
  getFullName as computeFullName,
} from "../utils/userDisplay";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  ACCENT_TINT_STRONG,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  ICON,
  HAIRLINE,
  HAIRLINE_STRONG,
  SURFACE_MUTED,
  HOVER,
  SHADOW_LG,
  RADIUS,
  FONT,
  WEIGHT,
  withAlpha,
} from "../theme/tokens";

// Icons used in avatar menu
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

const CURRENT_VERSION = process.env.REACT_APP_VERSION || "dev";

// Center-nav links to the Docusaurus site
const NAV_LINK_SX = {
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  letterSpacing: "0.01em",
  color: "#334155",
  px: 0.5,
  minWidth: "auto",
  borderRadius: 0,
  position: "relative",
  backgroundColor: "transparent",
  boxShadow: "none",
  transition: "color 0.2s ease",
  "&:hover": {
    color: INK,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 2,
    height: "2px",
    width: "100%",
    backgroundColor: ACCENT_DARK,
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.2s ease",
  },
  "&:hover::after, &:focus-visible::after": { transform: "scaleX(1)" },
};

const TopBar = ({ user }) => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const menuId = "account-menu";
  const avatarBtnRef = useRef(null);

  const [navigationAnchor, setNavigationAnchor] = React.useState(null);
  const navigationOpen = Boolean(navigationAnchor);
  const closeNavigation = () => setNavigationAnchor(null);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const closeThen = (fn) => {
    try {
      document.activeElement?.blur();
      handleMenuClose();
    } finally {
      requestAnimationFrame(() => {
        avatarBtnRef.current?.focus();
        fn && fn();
      });
    }
  };

  const handleMenuNavigate = (path) => closeThen(() => navigate(path));
  const navigateToHomePage = () => navigate("/");

  const handleLogout = async () => {
    closeThen(async () => {
      try {
        await logoutGrafana();
        await supabase.auth.signOut();
      } catch (e) {
        console.error("signOut failed:", e);
      } finally {
        navigate("/", { replace: true });
      }
    });
  };

  // Resolve the current user's fields from profile, falling back to signup
  // metadata, then delegate display formatting to the shared helpers.
  const sp = profile?.profile || {};
  const meta = user?.user_metadata || {};
  const firstName = sp.first_name || meta.first_name || "";
  const lastName = sp.last_name || meta.last_name || "";
  const email = user?.email || "";

  const getInitials = () => computeInitials(firstName, lastName, email);
  const getFullName = () => computeFullName(firstName, lastName, email);

  const renderCenterNav = () => (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        gap: 3,
      }}
    >
      <Button
        component="a"
        href="/docs/"
        target="_blank"
        rel="noopener noreferrer"
        disableRipple
        disableFocusRipple
        aria-label="View Documentation"
        sx={NAV_LINK_SX}
      >
        Documentation
      </Button>
      <Button
        component="a"
        href="/docs/blog/"
        target="_blank"
        rel="noopener noreferrer"
        disableRipple
        disableFocusRipple
        aria-label="View Blogs"
        sx={NAV_LINK_SX}
      >
        Blogs
      </Button>
    </Box>
  );

  const renderMobileNav = () => (
    <>
      <IconButton
        aria-label="Open navigation menu"
        aria-controls={navigationOpen ? "primary-navigation-menu" : undefined}
        aria-haspopup="menu"
        aria-expanded={navigationOpen ? "true" : undefined}
        onClick={(event) => setNavigationAnchor(event.currentTarget)}
        sx={{
          display: { xs: "inline-flex", md: "none" },
          color: INK_SECONDARY,
        }}
      >
        <MenuRoundedIcon />
      </IconButton>
      <Menu
        id="primary-navigation-menu"
        anchorEl={navigationAnchor}
        open={navigationOpen}
        onClose={closeNavigation}
        MenuListProps={{ "aria-label": "Site navigation" }}
      >
        <MenuItem
          component="a"
          href="/docs/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeNavigation}
        >
          Documentation
        </MenuItem>
        <MenuItem
          component="a"
          href="/docs/blog/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeNavigation}
        >
          Blogs
        </MenuItem>
        <MenuItem component={NavLink} to="/changelog" onClick={closeNavigation}>
          Release notes (v{CURRENT_VERSION})
        </MenuItem>
      </Menu>
    </>
  );

  const renderActionArea = () => {
    if (!user) {
      return (
        <Button
          component={NavLink}
          to="/signin"
          disableElevation
          disableRipple
          startIcon={<LoginRoundedIcon sx={{ fontSize: "1.05rem" }} />}
          sx={{
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            px: { xs: 1.5, sm: 2.5 },
            py: 0.7,
            borderRadius: "8px",
            color: INK_SECONDARY,
            backgroundColor: "transparent",
            border: `1px solid ${HAIRLINE_STRONG}`,
            boxShadow: "none",
            transition:
              "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
            "& .MuiButton-startIcon": { mr: 0.75 },
            "&:hover": {
              backgroundColor: "#334155",
              borderColor: "#334155",
              color: "#fff",
              boxShadow: "none",
              transform: "none",
            },
            "&:active": {
              boxShadow: "none",
              transform: "none",
            },
            "&.Mui-focusVisible": {
              outline: "2px solid rgba(71,85,105,0.35)",
              outlineOffset: "2px",
            },
          }}
        >
          Sign in
        </Button>
      );
    }

    const initials = getInitials();

    return (
      <>
        <Tooltip title={user.email || "Account"}>
          <IconButton
            ref={avatarBtnRef}
            onClick={handleAvatarClick}
            aria-label="Open account menu"
            disableRipple
            sx={{
              p: 0.5,
              borderRadius: "999px",
              transition: "background-color 0.2s ease",
              "&:hover": { backgroundColor: HOVER },
              "&.Mui-focusVisible": {
                outline: "2px solid rgba(71,85,105,0.35)",
                outlineOffset: "2px",
              },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: ACCENT_DARK,
                color: "#fff",
                fontSize: "0.8rem",
                fontWeight: 500,
                letterSpacing: "0.02em",
                border: "1px solid rgba(15,23,42,0.06)",
              }}
            >
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => closeThen()}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          transitionDuration={0}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          MenuListProps={{
            autoFocusItem: false,
            "aria-labelledby": "account-button",
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1.2,
              minWidth: 268,
              overflow: "hidden",
              borderRadius: `${RADIUS.md}px`,
              border: `1px solid ${HAIRLINE}`,
              boxShadow: SHADOW_LG,
              // No top padding so the identity header sits flush with the
              // menu's rounded top edge (no white strip above its tinted band).
              "& .MuiList-root": { pt: 0, pb: 0.5 },
              "& .MuiMenuItem-root": {
                fontSize: FONT.md,
                color: INK,
                mx: 0.5,
                px: 1,
                py: 0.85,
                borderRadius: 1.5,
                "& .MuiListItemIcon-root": { minWidth: 32, color: ICON },
                "&:hover": { bgcolor: HOVER },
              },
            },
          }}
        >
          {/* Identity header */}
          <Box
            sx={{
              px: 2,
              py: 1.75,
              mb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: SURFACE_MUTED,
              borderBottom: `1px solid ${HAIRLINE}`,
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: ACCENT_DARK,
                color: "#fff",
                fontSize: "0.9rem",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: FONT.md,
                    fontWeight: WEIGHT.semibold,
                    color: INK,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getFullName()}
                </Typography>
                {user?.userRole === "admin" && (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: "999px",
                      bgcolor: ACCENT_TINT,
                      color: ACCENT_DARK,
                      fontSize: "0.65rem",
                      fontWeight: WEIGHT.bold,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      flexShrink: 0,
                    }}
                  >
                    Admin
                  </Box>
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: FONT.xs,
                  color: INK_MUTED,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>

          {user?.userRole === "admin" && (
            <MenuItem
              onClick={() =>
                handleMenuNavigate("/workspace?tab=admin/experiments")
              }
            >
              <ListItemIcon>
                <DashboardCustomizeOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Admin Dashboard
            </MenuItem>
          )}
          {user?.userRole === "admin" && <Divider sx={{ my: 0.5 }} />}

          <MenuItem
            onClick={() =>
              handleMenuNavigate("/workspace?tab=overview/compute")
            }
          >
            <ListItemIcon>
              <StorageRoundedIcon fontSize="small" />
            </ListItemIcon>
            Resources
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleMenuNavigate("/workspace?tab=experiments/list")
            }
          >
            <ListItemIcon>
              <ScienceRoundedIcon fontSize="small" />
            </ListItemIcon>
            My Experiments
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleMenuNavigate("/workspace?tab=experiments/create")
            }
          >
            <ListItemIcon>
              <AddCircleOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            Create a Workspace
          </MenuItem>

          <MenuItem
            onClick={() => handleMenuNavigate("/workspace?tab=jobs/submit")}
          >
            <ListItemIcon>
              <AddCircleOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            Submit a Job
          </MenuItem>

          <MenuItem
            onClick={() =>
              handleMenuNavigate("/workspace?tab=settings/profile")
            }
          >
            <ListItemIcon>
              <SettingsRoundedIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onClick={handleLogout}
            sx={{
              color: "error.main",
              "& .MuiListItemIcon-root": { color: "error.main" },
              "&:hover": { bgcolor: "rgba(209,67,67,0.08) !important" },
            }}
          >
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: "#ffffff",
        borderBottom: "1px solid rgba(16,24,40,0.10)",
        boxShadow: "none",
        height: 64,
        justifyContent: "center",
        zIndex: 1100,
      }}
    >
      <Toolbar
        sx={{
          position: "relative",
          minHeight: 64,
          px: { xs: 2, sm: 4 },
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 0.85 },
            }}
            onClick={navigateToHomePage}
          >
            <img
              src={logoNoText}
              alt="EFabric"
              style={{ height: 28, display: "block" }}
            />
            <Typography
              sx={{
                fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "1.35rem",
                letterSpacing: "0.01em",
                color: ACCENT_DARK,
                lineHeight: 1,
              }}
            >
              EFabric
            </Typography>
          </Box>

          {/* Divider between brand and version */}
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              width: "1px",
              height: 22,
              bgcolor: "rgba(16,24,40,0.14)",
            }}
          />

          <Tooltip title="View release notes">
            <Button
              size="small"
              disableRipple
              onClick={() => navigate("/changelog")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                lineHeight: 1.4,
                px: 1,
                py: "2px",
                borderRadius: `${RADIUS.sm}px`,
                minWidth: "auto",
                color: ACCENT_DARK,
                backgroundColor: ACCENT_TINT_STRONG,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: withAlpha(ACCENT, 0.26),
                  boxShadow: "none",
                },
              }}
            >
              v{CURRENT_VERSION}
            </Button>
          </Tooltip>
        </Box>

        {/* Center: primary navigation */}
        {renderCenterNav()}

        {/* Right: account / sign in */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 2 },
          }}
        >
          {renderMobileNav()}
          {renderActionArea()}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    userRole: PropTypes.string,
    user_metadata: PropTypes.object,
  }),
};

export default TopBar;
